// @givmo/fundraisers-module — the Fundraiser Creation Wizard (EMC-WEB7; D-EMC-19, Jonathan 2026-10-28):
// "an obvious Fundraiser Creation Wizard ... the wizard should have one field per screen steps". Built
// behind the host's build knob (`config.creationWizard`).
//
// The order is Jonathan's of 2026-10-30 (campaign-structure SPEC.md §18): the choice first, "One page,
// or a campaign?". A campaign then asks "Will people raise in groups?" (only where the host's knob offers
// groups, `config.campaignGroups`), then "Set up your groups" on Yes or "Who can create a fundraiser
// page?" on No (or with no groups offered). Then the name, the goal, the story and the photo. There is
// no end-date step: the end is set in the full editor. "Just this page" takes five steps; a campaign
// seven, or six where groups aren't offered (the groups question is the one left out).
//
// Every write is one the module already makes:
//   - the answers before the name are held here: nothing is saved before it;
//   - the name's act creates the draft (`POST /org/{org_ref}/fundraisers`) and, on a campaign, makes it
//     one in the same act (`POST /fundraisers/{id}/campaign`, carrying who can create a fundraiser page;
//     on Yes, "Only people you invite" for pages on the campaign itself, since each group runs its own);
//     a campaign never goes back, so its button is "Save and continue", saying so under it;
//   - the same act says whether it has teams, always: the server's default is on, and a campaign has
//     them only if she answered Yes, which she can't where groups aren't offered. Fundraisers can start
//     one only on "Anyone can start one";
//   - on Yes, what the groups are called is the campaign's name for what's inside it (`layer_name`),
//     written after the enable only where the enable's answer serves that key. How members raise, and
//     who runs each one beyond "Anyone can start one", have no home on the server yet, so they are asked
//     and held, never written;
//   - the goal and the story are the editor's own PATCH; the cover is the editor's upload, which needs
//     the draft, so the photo is last.
// The wizard never publishes (Jonathan: "there are too many other things the user might want to set
// up before they actually publish it"): Finish opens the draft in the full editor's Details tab with
// "Draft saved", and a draft reopened from the list opens the editor, not the wizard.
//
// Leaving: before the name's act the top bar says "Exit" and nothing is saved. Once the draft exists it
// says "Skip to full editor | Save and exit" (no Skip on the last step), and both save what she has
// answered first. Leaving never makes a campaign: only the name's act does, where its note says so.

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { orgFundraisersListOrgFundraisersQueryKey } from '@givmo/api-client/query';
import {
  isGivmoError,
  sdk,
  type OrgFundraiserOut,
  type OrgFundraiserUpdate,
  type P2pConfigRequest,
} from '@givmo/api-client';
import { Select, toast } from '@givmo/design-system';
import { useFundraisersModule } from './fundraisers-context';
import { FUNDRAISERS_ROUTES } from './fundraisers-routes';
import {
  fundraiserOptions,
  privateFundraiserOptions,
  requireOrgFundraiser,
} from './fundraisers-queries';
import { campaignOptions } from './campaign-queries';
import {
  GOAL_MESSAGE,
  NAME_MAX,
  STORY_MAX,
  canonicalGoal,
  cleanGoal,
  displayGoal,
  isGoalish,
} from './fundraiser-form';
import {
  COVER_ACCEPT,
  COVER_MAX_BYTES,
  COVER_TOO_LARGE_MESSAGE,
  COVER_WRONG_TYPE_MESSAGE,
  isAcceptableCover,
} from './cover';
import {
  LAYER_NAME_MAX_LENGTH,
  collapseLayerName,
  layerNameBody,
  layerNameLive,
} from './layers-decode';
import { REQUESTS_COPY, type JoinPolicyValue } from './join-requests-copy';
import { useLivePage } from './use-live-page';
import {
  COLLATERAL_COVER_COPY,
  CollateralCoverDialog,
  applyCollateralCover,
  useCoverCollateral,
} from './collateral-cover';
import type { CollateralItem } from '@givmo/org-console';
import { GROUP_NAMES, WIZARD_COPY as C } from './creation-wizard-copy';
import {
  CampaignArt,
  ChoiceCard,
  CoverFrame,
  NEXT_ARROW,
  OnePageArt,
  OptionRow,
  SetupCard,
  SetupSection,
  SmallOption,
  StageButton,
  StageField,
  StageTextArea,
  WizardActions,
  WizardQuestion,
  WizardStage,
} from './creation-wizard-stage';

type Kind = 'page' | 'campaign';
/** "No, just fundraiser pages" or "Yes, in groups". */
type Groups = 'no' | 'yes';
/** Who runs each group: someone the organization invites, its own admins, or anyone with the link. */
type Runs = 'invite' | 'admins' | 'anyone';
/** How group members raise: each on their own fundraiser page, or all on the group's. */
type Raise = 'own' | 'shared';
/** A name from the list (its plural), or a write-in. */
type GroupName = (typeof GROUP_NAMES)[number]['plural'] | 'other';
type StepId = 'kind' | 'groups' | 'setup' | 'pages' | 'name' | 'goal' | 'story' | 'cover';
type Join = Exclude<JoinPolicyValue, 'members_only'>;
type Direction = 'forward' | 'back';

/** Who can create a supporter page, in the order the frames draw them (a charity offers no members). */
const JOINS: readonly Join[] = ['open', 'request_to_join', 'invite_only'];
const OTHER = 'other';

/** How long the outgoing question stays on screen: the 250ms shared-axis move, and a frame to spare. */
const STEP_MS = 270;

/**
 * The steps her path takes (SPEC.md §18). Before the choice only the choice is known, so the page's
 * path stands in. A campaign's third step is "Set up your groups" on Yes and "Who can create a
 * fundraiser page?" otherwise, so both of its answers count seven, and the count holds before step 2
 * is answered. Where groups aren't offered, the groups question is left out: six.
 */
export function wizardPath(
  kind: Kind | null,
  asksGroups: boolean,
  groups: Groups | null,
): StepId[] {
  const content: StepId[] = ['name', 'goal', 'story', 'cover'];
  if (kind !== 'campaign') return ['kind', ...content];
  if (!asksGroups) return ['kind', 'pages', ...content];
  return ['kind', 'groups', groups === 'yes' ? 'setup' : 'pages', ...content];
}

interface Answers {
  kind: Kind | null;
  groups: Groups | null;
  groupName: GroupName;
  /** The write-in under "Other…". */
  otherName: string;
  runs: Runs;
  raise: Raise;
  join: Join;
  name: string;
  goal: string;
  story: string;
}

/** What the campaign holds once made, so a later change is a settings write of the difference. */
interface Made {
  join: Join;
  /** Whether the campaign has teams, and whether fundraisers can start one. */
  teams: boolean;
  supporterTeams: boolean;
  /** The plural written as its `layer_name`, if any. */
  layerName: string | null;
  /** The settings answer carries `layer_name` only while the backend serves layer names. */
  layerNameLive: boolean;
}

/** A refusal said in the server's words, or the editor's own line. */
function refusalWords(caught: unknown, fallback: string): string {
  return isGivmoError(caught) && caught.message ? caught.message : fallback;
}

/** A goal she typed that the editor's rule refuses: said where she presses, and never written. */
class GoalRefused extends Error {}

/** The content she has answered that the draft doesn't hold yet, as the editor's minimal PATCH. */
function contentChanges(row: OrgFundraiserOut, answers: Answers): OrgFundraiserUpdate | null {
  const body: OrgFundraiserUpdate = {};
  const name = answers.name.trim();
  if (name.length > 0 && name !== row.name) body.name = name;
  const goal = cleanGoal(answers.goal);
  if (canonicalGoal(goal) !== canonicalGoal(row.goal_amount ?? ''))
    body.goal_amount = goal.length > 0 ? goal : null;
  const story = answers.story.trim();
  if (story !== (row.story ?? '')) body.story = story.length > 0 ? story : null;
  return Object.keys(body).length > 0 ? body : null;
}

/** A write-in's singular, for the name's two forms: its last "s" off ("Troops" → "Troop"). The full
 * editor's Group names card holds both forms, so a wrong guess is fixed there. */
export function singularOf(plural: string): string {
  const name = collapseLayerName(plural);
  return /[^s]s$/i.test(name) ? name.slice(0, -1) : name;
}

/** The groups' name, both forms, as answered; null for a write-in left empty. */
function groupForms(answers: Answers): { singular: string; plural: string } | null {
  if (answers.groupName !== OTHER) {
    const picked = GROUP_NAMES.find((item) => item.plural === answers.groupName);
    return picked ? { singular: picked.singular, plural: picked.plural } : null;
  }
  const plural = collapseLayerName(answers.otherName);
  return plural.length > 0 ? { singular: singularOf(plural), plural } : null;
}

export function FundraiserCreationWizard() {
  const config = useFundraisersModule();
  // The page that renders the wizard does so only for a writable, resolved organization.
  const orgRef = config.orgRef ?? '';
  const asksGroups = config.campaignGroups === true;
  const client = useQueryClient();
  const live = useLivePage();
  const base = useId();
  const [step, setStep] = useState<StepId>('kind');
  const [answers, setAnswers] = useState<Answers>({
    kind: null,
    groups: null,
    groupName: 'Teams',
    otherName: '',
    runs: 'invite',
    raise: 'own',
    join: 'open',
    name: '',
    goal: '',
    story: '',
  });
  const [draft, setDraft] = useState<OrgFundraiserOut | null>(null);
  const [made, setMade] = useState<Made | null>(null);
  const [cover, setCover] = useState<{ src: string; uploading: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** The question leaving, with its own step label: a changed answer can change the path mid-move. */
  const [leaving, setLeaving] = useState<{
    step: StepId;
    direction: Direction;
    stepLabel: string;
  } | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const focusTarget = useRef<HTMLElement | null>(null);
  const firstInput = useRef<HTMLElement | null>(null);
  const moved = useRef(false);
  const coverSrc = useRef<string | null>(null);

  const inGroups = asksGroups && answers.groups === 'yes';
  const path = wizardPath(answers.kind, asksGroups, answers.groups);
  const at = path.indexOf(step);
  const stepNumber = at + 1;
  // No total before the choice: step 1 reads "Step 1" until a card is chosen.
  const total = answers.kind !== null ? path.length : null;
  const last = step === 'cover';
  const uploading = cover?.uploading === true;
  // The organization's Collateral, offered beside the upload while it holds a current image.
  const library = useCoverCollateral(config.orgRef);
  const [choosing, setChoosing] = useState(false);
  /** On Yes, pages on the campaign itself are by invitation: each group runs its own. */
  const join: Join = inGroups ? 'invite_only' : answers.join;
  /** Teams are hers to choose: only Yes turns them on, and only "Anyone" lets fundraisers start one. */
  const teams = inGroups;
  const supporterTeams = inGroups && answers.runs === 'anyone';

  useEffect(
    () => () => {
      window.clearTimeout(timer.current);
      if (coverSrc.current) URL.revokeObjectURL(coverSrc.current);
    },
    [],
  );
  // Each new question takes the focus at its own field, so a screen reader hears the question it asks.
  useEffect(() => {
    if (!moved.current) return;
    focusTarget.current?.focus({ preventScroll: true });
  }, [step]);

  const unsaved =
    draft === null
      ? answers.kind !== null || answers.name.trim().length > 0
      : contentChanges(draft, answers) !== null;
  useEffect(() => {
    if (!unsaved) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [unsaved]);

  function answer<K extends keyof Answers>(key: K, value: Answers[K]) {
    setAnswers((previous) => ({ ...previous, [key]: value }));
    setError(null);
  }

  function go(to: StepId, direction: Direction) {
    window.clearTimeout(timer.current);
    moved.current = true;
    setLeaving({ step, direction, stepLabel: C.stepOf(stepNumber, total) });
    setStep(to);
    setError(null);
    timer.current = window.setTimeout(() => setLeaving(null), STEP_MS);
  }

  async function run(task: () => Promise<void>) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await task();
    } catch (caught) {
      if (live()) refused(caught);
    } finally {
      if (live()) setBusy(false);
    }
  }

  /**
   * Says a refusal where she can act on it. A goal she typed and then went Back from holds the way out
   * on an earlier step, so the goal's own question takes her back to say it. Anything else is said on
   * the step, and its field takes the focus back: the act gave it up when it was disabled to save.
   */
  function refused(caught: unknown) {
    if (caught instanceof GoalRefused && step !== 'goal') {
      go('goal', path.indexOf('goal') > at ? 'forward' : 'back');
      setError(GOAL_MESSAGE);
      return;
    }
    setError(caught instanceof GoalRefused ? GOAL_MESSAGE : refusalWords(caught, C.saveFailed));
    focusTarget.current?.focus({ preventScroll: true });
  }

  async function patch(row: OrgFundraiserOut, body: OrgFundraiserUpdate) {
    const { data } = await sdk.orgFundraisersUpdateOrgFundraiser({
      path: { org_ref: orgRef, fundraiser_id: row.id },
      body,
      throwOnError: true,
    });
    const next = requireOrgFundraiser(data, orgRef);
    if (live()) setDraft(next);
    return next;
  }

  /** The step's own content answer (name, goal or story), saved when it changed. */
  async function saveContent(
    row: OrgFundraiserOut,
    only: 'name' | 'goal' | 'story' | 'all',
    given: Answers = answers,
  ) {
    if ((only === 'goal' || only === 'all') && !isGoalish(given.goal)) throw new GoalRefused();
    const changes = contentChanges(row, given);
    const key = only === 'goal' ? 'goal_amount' : only;
    const body: OrgFundraiserUpdate | null =
      changes === null || key === 'all'
        ? changes
        : key in changes
          ? // One key of the minimal PATCH: the server applies only the keys it is given.
            ({ [key]: changes[key] } as OrgFundraiserUpdate)
          : null;
    if (body === null) return row;
    const saved = await patch(row, body);
    if (only === 'goal' && live()) answer('goal', displayGoal(saved.goal_amount));
    return saved;
  }

  /**
   * The name's act on a campaign: makes the campaign with the answers held before it, in one call, then
   * names its groups where the server serves the name. Once it is made, writes what she changed since.
   * The campaign is recorded as made the moment the enable succeeds, before the name's write: a refused
   * name write then leaves a retry to write only the name, never a second enable the server refuses.
   */
  async function makeCampaign(row: OrgFundraiserOut) {
    if (made === null) {
      const { data } = await sdk.campaignsEnableCampaign({
        path: { fundraiser_id: row.id },
        body: {
          join_policy: join,
          teams_enabled: teams,
          supporter_teams_allowed: supporterTeams,
        },
        throwOnError: true,
      });
      const enabled: Made = {
        join,
        teams,
        supporterTeams,
        layerName: null,
        layerNameLive: layerNameLive(data),
      };
      if (live()) setMade(enabled);
      await updateCampaign(row, enabled);
      return;
    }
    await updateCampaign(row);
  }

  /** What she changed in the campaign's held answers since it was made, as one settings write. */
  async function updateCampaign(row: OrgFundraiserOut, held: Made | null = made) {
    if (held === null) return;
    const forms = inGroups ? groupForms(answers) : null;
    const body: P2pConfigRequest = {};
    if (join !== held.join) body.join_policy = join;
    if (teams !== held.teams) body.teams_enabled = teams;
    if (supporterTeams !== held.supporterTeams) body.supporter_teams_allowed = supporterTeams;
    if (forms !== null && held.layerNameLive && forms.plural !== held.layerName)
      Object.assign(body, layerNameBody(forms.singular, forms.plural));
    if (Object.keys(body).length === 0) return;
    await sdk.campaignsUpdateCampaign({
      path: { fundraiser_id: row.id },
      body,
      throwOnError: true,
    });
    if (live())
      setMade({
        ...held,
        join,
        teams,
        supporterTeams,
        layerName: body.layer_name ? (forms?.plural ?? held.layerName) : held.layerName,
      });
  }

  /** The reads the editor and the list will make, marked stale so they show what was just saved. */
  async function refreshReads(id: string) {
    await Promise.all([
      client.invalidateQueries({
        queryKey: orgFundraisersListOrgFundraisersQueryKey({ path: { org_ref: orgRef } }),
      }),
      client.invalidateQueries({ queryKey: fundraiserOptions(orgRef, id).queryKey }),
      client.invalidateQueries({ queryKey: privateFundraiserOptions(id).queryKey }),
      client.invalidateQueries({ queryKey: campaignOptions(id).queryKey }),
    ]);
  }

  /** Every answer the draft doesn't hold yet, then the reads marked stale. */
  async function saveAll(row: OrgFundraiserOut) {
    const saved = await saveContent(row, 'all');
    await updateCampaign(saved);
    await refreshReads(saved.id);
    return saved;
  }

  /**
   * Leaves the wizard: to the list ("Exit", "Save and exit") or the editor's Details tab ("Skip to full
   * editor"), saving every answer first once a draft exists.
   */
  function leave(to: 'list' | 'editor') {
    void run(async () => {
      const row = draft === null ? null : await saveAll(draft);
      if (!live()) return;
      config.onNavigate(
        to === 'editor' && row !== null
          ? FUNDRAISERS_ROUTES.editor(row.id)
          : FUNDRAISERS_ROUTES.list,
      );
      if (row !== null && to === 'list') toast.success(C.saved);
    });
  }

  function forward() {
    const to = path[path.indexOf(step) + 1];
    if (to) go(to, 'forward');
  }

  function next() {
    void run(async () => {
      if (step === 'kind') {
        if (answers.kind === null) return;
      } else if (step === 'groups') {
        if (answers.groups === null) return;
      } else if (step === 'setup') {
        if (groupForms(answers) === null) return;
      } else if (step === 'name') {
        const name = answers.name.trim();
        if (name.length === 0) return;
        let row = draft;
        if (row === null) {
          const { data } = await sdk.orgFundraisersCreateOrgFundraiser({
            path: { org_ref: orgRef },
            // Today's create body: the name, and the totals shown (changed only in the editor).
            body: { name, show_statistics: true },
            throwOnError: true,
          });
          row = requireOrgFundraiser(data, orgRef);
          if (live()) setDraft(row);
        } else {
          row = await saveContent(row, 'name');
        }
        // A refused enable keeps her here, on a draft that is still one page: Back can choose that.
        if (answers.kind === 'campaign') await makeCampaign(row);
      } else if (step === 'goal' && draft !== null) {
        await saveContent(draft, 'goal');
      } else if (step === 'story' && draft !== null) {
        await saveContent(draft, 'story');
      } else if (step === 'cover') {
        // Finish: everything she answered, then the full editor, where she publishes.
        if (draft === null) return;
        const row = await saveAll(draft);
        if (!live()) return;
        config.onNavigate(FUNDRAISERS_ROUTES.editor(row.id));
        toast.success(C.saved);
        return;
      }
      if (live()) forward();
    });
  }

  /** "No goal": goes on without one, clearing any she typed. */
  function noGoal() {
    void run(async () => {
      answer('goal', '');
      if (draft !== null) await saveContent(draft, 'goal', { ...answers, goal: '' });
      if (live()) forward();
    });
  }

  function back() {
    const to = path[at - 1];
    if (to) go(to, 'back');
  }

  /** A Collateral image as the cover: the server copies it through the cover upload. */
  function pickCollateral(item: CollateralItem) {
    setChoosing(false);
    if (draft === null || uploading || config.orgRef === null) return;
    const previous = cover;
    setCover({ src: item.url, uploading: true });
    setError(null);
    applyCollateralCover(config.orgRef, item.id, draft.id)
      .then(() => {
        if (previous?.src.startsWith('blob:')) URL.revokeObjectURL(previous.src);
        if (coverSrc.current === previous?.src) coverSrc.current = null;
        if (live()) setCover({ src: item.url, uploading: false });
      })
      .catch((caught: unknown) => {
        if (!live()) return;
        setCover(previous);
        setError(refusalWords(caught, COLLATERAL_COVER_COPY.failed));
      });
  }

  function pickCover(file: File) {
    if (draft === null || uploading) return;
    if (!isAcceptableCover(file)) {
      setError(COVER_WRONG_TYPE_MESSAGE);
      return;
    }
    if (file.size > COVER_MAX_BYTES) {
      setError(COVER_TOO_LARGE_MESSAGE);
      return;
    }
    const previous = cover;
    const src = URL.createObjectURL(file);
    coverSrc.current = src;
    setCover({ src, uploading: true });
    setError(null);
    // The upload starts as soon as she picks: the draft exists, so it goes straight to it.
    sdk
      .fundraisersUploadFundraiserPicture({
        path: { fundraiser_id: draft.id },
        body: { file },
        throwOnError: true,
      })
      .then(() => {
        if (previous) URL.revokeObjectURL(previous.src);
        if (live()) setCover({ src, uploading: false });
      })
      .catch((caught: unknown) => {
        URL.revokeObjectURL(src);
        coverSrc.current = previous?.src ?? null;
        if (!live()) return;
        setCover(previous);
        setError(refusalWords(caught, C.uploadFailed));
      });
  }

  /** Registers the field a step's question focuses, on the live pane only. */
  const focusRef = (element: HTMLElement | null) => {
    if (element) focusTarget.current = element;
  };
  /**
   * The first question takes the dialog's first focus at its heading, so a screen reader hears it and
   * neither card wears a focus ring before she has chosen; Tab then reaches the cards.
   */
  const firstRef = (element: HTMLElement | null) => {
    if (element) firstInput.current = element;
  };

  function view(id: StepId, isLive: boolean, stepLabel: string): ReactNode {
    const titleId = `${base}-${id}-title`;
    const lineId = `${base}-${id}-line`;
    const ref = isLive ? focusRef : undefined;
    const testId = (name: string) => (isLive ? name : undefined);
    const question = (title: string, line?: string, titleFocus = false) => (
      <WizardQuestion
        stepLabel={stepLabel}
        stepTestId={testId('fundraiser-wizard-step')}
        title={title}
        titleId={titleId}
        titleRef={titleFocus ? ref : undefined}
        line={line}
        lineId={lineId}
      />
    );
    const nextButton = (label: string, options: { disabled?: boolean; arrow?: boolean } = {}) => (
      <StageButton
        type="submit"
        trailing={options.arrow === false ? undefined : NEXT_ARROW}
        disabled={options.disabled}
        busy={isLive && busy}
        testId={testId('fundraiser-wizard-next')}
      >
        {label}
      </StageButton>
    );
    const actions = (act: ReactNode, note?: string) => (
      <WizardActions
        onBack={id === 'kind' ? undefined : back}
        backDisabled={busy || uploading}
        backTestId={testId('fundraiser-wizard-back')}
        note={note}
        error={isLive ? error : null}
      >
        {act}
      </WizardActions>
    );
    let body: ReactNode;
    switch (id) {
      case 'kind': {
        const lockedToCampaign = made !== null;
        body = (
          <>
            <WizardQuestion
              stepLabel={stepLabel}
              stepTestId={testId('fundraiser-wizard-step')}
              title={C.kindTitle}
              titleId={titleId}
              titleRef={isLive ? firstRef : undefined}
            />
            <div
              className="mt-32 flex gap-24 max-sm:flex-col max-sm:gap-16"
              role="radiogroup"
              aria-labelledby={titleId}
            >
              <ChoiceCard
                name={`${base}-kind`}
                value="page"
                art={<OnePageArt />}
                label={C.pageLabel}
                line={C.pageLine}
                chosen={answers.kind === 'page'}
                disabled={lockedToCampaign}
                onSelect={() => answer('kind', 'page')}
                inputRef={answers.kind === 'campaign' ? undefined : ref}
                testId={testId('fundraiser-wizard-kind-page')}
              />
              <ChoiceCard
                name={`${base}-kind`}
                value="campaign"
                art={<CampaignArt />}
                label={C.campaignLabel}
                line={C.campaignLine}
                chosen={answers.kind === 'campaign'}
                onSelect={() => answer('kind', 'campaign')}
                inputRef={answers.kind === 'campaign' ? ref : undefined}
                testId={testId('fundraiser-wizard-kind-campaign')}
              />
            </div>
            {actions(nextButton(C.next, { disabled: answers.kind === null }))}
          </>
        );
        break;
      }
      case 'groups':
        // Two plain cards of one size, nothing opening inside them (Jonathan, 2026-10-29).
        body = (
          <>
            {question(C.groupsTitle)}
            <div
              className="mt-32 flex gap-24 max-sm:flex-col max-sm:gap-16"
              role="radiogroup"
              aria-labelledby={titleId}
            >
              <ChoiceCard
                name={`${base}-groups`}
                value="no"
                label={C.noGroupsLabel}
                line={C.noGroupsLine}
                chosen={answers.groups === 'no'}
                onSelect={() => answer('groups', 'no')}
                inputRef={answers.groups === 'yes' ? undefined : ref}
                testId={testId('fundraiser-wizard-groups-no')}
              />
              <ChoiceCard
                name={`${base}-groups`}
                value="yes"
                label={C.groupsLabel}
                line={C.groupsLine}
                chosen={answers.groups === 'yes'}
                onSelect={() => answer('groups', 'yes')}
                inputRef={answers.groups === 'yes' ? ref : undefined}
                testId={testId('fundraiser-wizard-groups-yes')}
              />
            </div>
            {actions(nextButton(C.next, { disabled: answers.groups === null }))}
          </>
        );
        break;
      case 'setup': {
        // Three questions in one card (Jonathan's ruling, 2026-10-29). The list alone names them; the
        // singular and plural are set in the full editor.
        const picked = GROUP_NAMES.find((item) => item.plural === answers.groupName);
        const singular = (groupForms(answers)?.singular ?? 'group').toLowerCase();
        const runs: { value: Runs; label: string; line: string }[] = [
          {
            value: 'invite',
            label: C.runsInvite(picked?.runner ?? 'a leader'),
            line: C.runsInviteLine(singular),
          },
          { value: 'admins', label: C.runsAdmins, line: C.runsAdminsLine(singular) },
          { value: 'anyone', label: C.runsAnyone, line: C.runsAnyoneLine },
        ];
        const raises: { value: Raise; label: string }[] = [
          { value: 'own', label: C.raiseOwn },
          { value: 'shared', label: C.raiseShared },
        ];
        body = (
          <>
            {question(C.setupTitle, undefined, true)}
            <div className="mt-28">
              <SetupCard>
                <SetupSection title={C.calledTitle} titleId={`${base}-called`}>
                  <div className="flex gap-12 max-sm:flex-col">
                    <Select
                      aria-label={C.calledTitle}
                      options={[
                        ...GROUP_NAMES.map((item) => ({ value: item.plural })),
                        { value: OTHER, label: C.otherLabel },
                      ]}
                      value={answers.groupName}
                      onValueChange={(value) => {
                        if (value) answer('groupName', value as GroupName);
                      }}
                      className="w-[calc(50%-6px)] max-sm:w-full"
                      testId={testId('fundraiser-wizard-group-name')}
                    />
                    {answers.groupName === OTHER ? (
                      <input
                        value={answers.otherName}
                        onChange={(event) => answer('otherName', event.target.value)}
                        maxLength={LAYER_NAME_MAX_LENGTH}
                        placeholder={C.otherPlaceholder}
                        aria-label={C.calledTitle}
                        autoComplete="off"
                        className="min-w-0 flex-1 rounded-all12 border border-neutral-500 bg-surface p-12 text-s16 font-w600 text-text-strong caret-brand-500 outline-none placeholder:font-w500 placeholder:text-text-tertiary focus-visible:border-brand-500 focus-visible:shadow-focus-glow"
                        data-testid={testId('fundraiser-wizard-group-name-other')}
                      />
                    ) : null}
                  </div>
                </SetupSection>
                <SetupSection title={C.runsTitle} titleId={`${base}-runs`} divided>
                  <div
                    className="grid grid-cols-3 gap-12 max-md:grid-cols-1"
                    role="radiogroup"
                    aria-labelledby={`${base}-runs`}
                  >
                    {runs.map((option) => (
                      <SmallOption
                        key={option.value}
                        name={`${base}-runs`}
                        value={option.value}
                        label={option.label}
                        line={option.line}
                        chosen={answers.runs === option.value}
                        onSelect={() => answer('runs', option.value)}
                        testId={testId(`fundraiser-wizard-runs-${option.value}`)}
                      />
                    ))}
                  </div>
                </SetupSection>
                <SetupSection title={C.raiseTitle} titleId={`${base}-raise`} divided>
                  <div
                    className="grid grid-cols-2 gap-12 max-sm:grid-cols-1"
                    role="radiogroup"
                    aria-labelledby={`${base}-raise`}
                  >
                    {raises.map((option) => (
                      <SmallOption
                        key={option.value}
                        name={`${base}-raise`}
                        value={option.value}
                        label={option.label}
                        chosen={answers.raise === option.value}
                        onSelect={() => answer('raise', option.value)}
                        testId={testId(`fundraiser-wizard-raise-${option.value}`)}
                      />
                    ))}
                  </div>
                </SetupSection>
              </SetupCard>
            </div>
            {actions(nextButton(C.next, { disabled: groupForms(answers) === null }))}
          </>
        );
        break;
      }
      case 'pages': {
        const joinRef = (option: Join) => (option === answers.join ? ref : undefined);
        body = (
          <>
            {/* The Campaign tab's join setting, asked as Jonathan put it (2026-10-28). */}
            {question(REQUESTS_COPY.whoCanJoin, REQUESTS_COPY.whoCanJoinScope)}
            <div
              className="mt-32 flex flex-col gap-16"
              role="radiogroup"
              aria-labelledby={titleId}
              aria-describedby={lineId}
            >
              {JOINS.map((option) => (
                <OptionRow
                  key={option}
                  name={`${base}-join`}
                  value={option}
                  label={REQUESTS_COPY.policyLabel[option]}
                  line={REQUESTS_COPY.policyDescription[option]}
                  chosen={answers.join === option}
                  onSelect={() => answer('join', option)}
                  inputRef={joinRef(option)}
                  testId={testId(`fundraiser-wizard-join-${option}`)}
                />
              ))}
            </div>
            {actions(nextButton(C.next))}
          </>
        );
        break;
      }
      case 'name': {
        const campaign = answers.kind === 'campaign';
        // On a campaign its act makes the campaign, which can't be undone, while it stays a draft: it
        // says both where she presses it, in words that don't sound like going live (Jonathan).
        const makes = campaign && made === null;
        body = (
          <>
            {question(campaign ? C.nameTitleCampaign : C.nameTitlePage, C.nameLine)}
            <div className="mt-32">
              <StageField
                value={answers.name}
                onChange={(value) => answer('name', value)}
                placeholder={C.namePlaceholder}
                maxLength={NAME_MAX}
                labelledBy={titleId}
                describedBy={lineId}
                inputRef={ref}
                testId={testId('fundraiser-wizard-name')}
              />
            </div>
            {actions(
              nextButton(makes ? C.saveAndContinue : C.next, {
                disabled: answers.name.trim().length === 0,
              }),
              makes ? C.campaignNote : undefined,
            )}
          </>
        );
        break;
      }
      case 'goal':
        body = (
          <>
            {question(C.goalTitle, answers.kind === 'campaign' ? C.goalLineCampaign : undefined)}
            <div className="mt-32">
              <StageField
                value={answers.goal}
                onChange={(value) => answer('goal', value)}
                prefix="$"
                inputMode="decimal"
                labelledBy={titleId}
                describedBy={answers.kind === 'campaign' ? lineId : undefined}
                invalid={isLive && error === GOAL_MESSAGE}
                inputRef={ref}
                testId={testId('fundraiser-wizard-goal')}
              />
            </div>
            {/* Optional: "No goal" goes on without one, in the old end-date step's link (§18). */}
            <div className="mt-16 flex justify-end">
              <button
                type="button"
                onClick={isLive ? noGoal : undefined}
                disabled={busy}
                className="rounded-all4 text-s17 font-w600 text-on-brand underline decoration-on-brand/60 underline-offset-[5px] outline-none focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-on-brand disabled:cursor-not-allowed"
                data-testid={testId('fundraiser-wizard-no-goal')}
              >
                {C.noGoal}
              </button>
            </div>
            {actions(nextButton(C.next))}
          </>
        );
        break;
      case 'story':
        body = (
          <>
            {question(C.storyTitle, C.storyLine)}
            <div className="mt-32">
              <StageTextArea
                value={answers.story}
                onChange={(value) => answer('story', value)}
                maxLength={STORY_MAX}
                labelledBy={titleId}
                describedBy={lineId}
                inputRef={ref}
                testId={testId('fundraiser-wizard-story')}
              />
            </div>
            {actions(nextButton(C.next))}
          </>
        );
        break;
      case 'cover':
        body = (
          <>
            {question(C.coverTitle, C.coverLine)}
            <div className="mt-32">
              <CoverFrame
                src={cover?.src ?? null}
                uploading={uploading}
                accept={COVER_ACCEPT}
                onFile={pickCover}
                onCollateral={library !== null ? () => setChoosing(true) : undefined}
                labelledBy={titleId}
                describedBy={lineId}
                buttonRef={ref}
                testIds={isLive}
              />
            </div>
            {/* The last step's act opens the draft in the editor, so there is no Skip beside it;
                it waits for the upload. */}
            {library !== null && (
              <CollateralCoverDialog
                open={choosing}
                onOpenChange={setChoosing}
                items={library}
                onChoose={pickCollateral}
              />
            )}
            {actions(nextButton(C.finish, { arrow: false, disabled: uploading }), C.finishNote)}
          </>
        );
        break;
    }
    return (
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (isLive) next();
        }}
        data-testid={testId(`fundraiser-wizard-step-${id}`)}
      >
        {body}
      </form>
    );
  }

  return (
    <WizardStage
      step={stepNumber}
      total={total}
      // "Exit" before anything is saved; from the draft on, every way out keeps what she answered.
      exitLabel={draft === null ? C.exit : C.saveAndExit}
      onExit={() => leave('list')}
      onSkip={draft !== null && !last ? () => leave('editor') : undefined}
      busy={busy || uploading}
      initialFocus={firstInput}
    >
      <div className="grid">
        {leaving !== null ? (
          <div
            key={`out-${leaving.step}`}
            className={`fundraiser-wizard-exit-${leaving.direction} pointer-events-none [grid-area:1/1]`}
            aria-hidden
            inert
          >
            {view(leaving.step, false, leaving.stepLabel)}
          </div>
        ) : null}
        <div
          key={`in-${step}`}
          className={
            leaving === null
              ? '[grid-area:1/1]'
              : `fundraiser-wizard-enter-${leaving.direction} [grid-area:1/1]`
          }
        >
          {view(step, true, C.stepOf(stepNumber, total))}
        </div>
      </div>
    </WizardStage>
  );
}
